import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
    ClassicEditor,
    Essentials,
    EventInfo,
    Paragraph,
    Undo,
} from 'ckeditor5'
import {
    Comments,
    SuggestionData,
    TrackChanges,
    TrackChangesData,
} from 'ckeditor5-premium-features'
import 'ckeditor5-premium-features/ckeditor5-premium-features.css'
import 'ckeditor5/ckeditor5.css'
import { useChatProfileStore } from 'components/AskDelaChat/ChatProfileStore'
import { processEnv } from 'components/configuration/environmentVariables'
import { useHighlightStoreShallowSelector } from 'components/WorkspaceEditor/HighlightStore'
import { useSuggestionsStore } from 'components/WorkspaceEditor/SuggestionStore'
import React, { memo, useCallback } from 'react'
import { useTrackChangeCards } from '../TrackChangesPanel/hooks/useTrackChangeCards'
import { ContentEditorLayout } from './ContentEditorLayout'
import {
    hideBalloonPanelsOnMutation,
    hideEvaluationBadge,
    HIGHLIGHT_CLASS,
} from './contentUtils'
import { TextIdPlugin, TrackChangePlugin, UserPlugin } from './model'

/**
 * A type of callback function to be called when the editor is ready
 * @param editor - The editor instance
 */
export type ClassicEditorOnReadyHandler = (editor: ClassicEditor) => void

/**
 * A type of callback function to be called when the editor loses focus
 */
export type ClassicEditorHandler = (
    /** The event information */
    event: EventInfo,
    /** The editor instance */
    editor: ClassicEditor
) => void

/** Props for the ContentEditor component */
export interface ContentEditorProps {
    /** Initial editor data in HTML format */
    initialData?: string
    /** Initial track changes (suggestions) to load into the editor (this is also used for collaborators configuration) */
    initialSuggestions?: SuggestionData[]
    handlers?: {
        /** Callback function to be called when the editor is ready */
        onReady?: ClassicEditorOnReadyHandler
        /** Callback function to be called when the editor loses focus */
        onBlur?: ClassicEditorHandler
        /** Callback function to be called when the editor changes */
        onChange?: ClassicEditorHandler
    }
}

/** Builds the configuration for the CKEditor:
 * - licenseKey (CKEditor license key)
 * - suggestions (track changes initial suggestions)
 * - plugins (CKEditor and custom plugins)
 * - me (Currently logged-in user configuration)
 * - comments (Comments plugin configuration)
 */
const buildConfig = (suggestions?: SuggestionData[]) => {
    const chatUser = useChatProfileStore((state) => state.chatUser)

    return {
        licenseKey: processEnv.REACT_APP_CKEditorLicenseKey,
        suggestions,
        plugins: [
            Essentials,
            Paragraph,
            Undo,
            UserPlugin,
            Comments,
            TrackChanges,
            TrackChangePlugin,
            TrackChangesData,
            TextIdPlugin,
        ],
        me: {
            id: chatUser?.id ?? '',
            name: `${chatUser?.profileInfo?.firstName}${chatUser?.profileInfo?.lastName}`.toUpperCase(),
        },
        comments: {
            editorConfig: {
                extraPlugins: [Comments],
                sidebar: {
                    container: document.body,
                },
            },
        },
    }
}

/**
 * A memoized ContentEditor component.
 * Create an instance of the ContentEditor component with overridden layout.
 * @param {ContentEditorProps} props - The props for the ContentEditor component.
 * @returns {React.ReactElement} Rendered ContentEditor component
 */
export const ContentEditor = memo<ContentEditorProps>(
    ({ initialData = '<p>:smiley:</p>', initialSuggestions, handlers }) => {
        // destructure callback handlers
        const { onReady, onBlur, onChange } = handlers || {}
        const suggestionCardFilter = useSuggestionsStore(
            (s) => s.suggestionCardFilter
        )
        const { isNotDefaultSuggestionCardFilter } = useTrackChangeCards()
        const requestHighlight = useHighlightStoreShallowSelector(
            (s) => s.requestHighlight
        )

        /** handles the CKEditor's onReady event:
         * - calls the onReady callback if provided
         * - disables the Enter key
         * - enables track changes by default
         * - prevents adding text before, after, above, or below block-level widgets like tables
         * - attaches the CKEditor inspector for debugging purposes
         */
        const onReadyHandler = useCallback(async (editor: ClassicEditor) => {
            onReady?.(editor)
            // disable the Enter key using the key name 'Enter'
            editor?.keystrokes?.set('Enter', (_, cancel) => {
                cancel()
            })
            // Enable track changes by default (TODO make it a prop we pass in)
            editor?.execute('trackChanges')

            // Prevent user from adding text before, after, above, or below block-level widgets like tables.
            // (Our tables are treated as a standalone unit, like our paragraphs)
            const widgetTypeAroundPlugin = editor?.plugins.get(
                'WidgetTypeAround'
            )
            widgetTypeAroundPlugin?.forceDisabled('OurApplication')
            // Attach inspector for debugging purposes.
            if (processEnv?.REACT_APP_CKEditorInspector === '1') {
                const { default: CKEditorInspector } = await import(
                    '@ckeditor/ckeditor5-inspector'
                )
                CKEditorInspector.attach(editor)
            }
        }, [])

        const handleFocus = useCallback(() => {
            hideBalloonPanelsOnMutation()
            if (initialSuggestions?.[0]?.id) {
                requestHighlight(initialSuggestions[0].id, HIGHLIGHT_CLASS)
            }

            // TODO: remove when prod license is available
            hideEvaluationBadge()
        }, [
            initialSuggestions,
            suggestionCardFilter,
            isNotDefaultSuggestionCardFilter,
            hideBalloonPanelsOnMutation,
            requestHighlight,
            hideEvaluationBadge,
        ])

        return (
            <ContentEditorLayout>
                <CKEditor
                    data-testid="content-editor"
                    editor={ClassicEditor}
                    config={buildConfig(initialSuggestions)}
                    data={initialData}
                    onReady={onReadyHandler}
                    onFocus={handleFocus}
                    onBlur={onBlur}
                    onChange={onChange}
                />
            </ContentEditorLayout>
        )
    }
)
